/**
 * 导演模式开关配置（管理后台）
 *
 * 控制前端「单图 / 多图 / 三合一」三种模式是否可用。
 */

import { useEffect, useState } from 'react';
import { Loader2, Save, ImageIcon, Layers, Sparkles } from 'lucide-react';
import toast from '../../Toast';
import {
  directorAdminApi,
  type ModeSwitchConfig,
} from '../../../apps/director/services/directorAdminApi';

const DEFAULT_FORM: ModeSwitchConfig = {
  keyframeEnabled: true,
  frameSequenceEnabled: true,
  trinityCardsEnabled: true,
};

export default function DirectorModeSwitchConfigTab() {
  const [form, setForm] = useState<ModeSwitchConfig>(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    directorAdminApi
      .getModeSwitchConfig()
      .then((cfg) => setForm(cfg))
      .catch((err) => toast.error(err instanceof Error ? err.message : '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  const update = <K extends keyof ModeSwitchConfig>(key: K, value: ModeSwitchConfig[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    // 至少保留一个模式
    if (!form.keyframeEnabled && !form.frameSequenceEnabled && !form.trinityCardsEnabled) {
      toast.error('至少需要开启一种导演模式');
      return;
    }
    setSaving(true);
    try {
      const patch: Partial<ModeSwitchConfig> = {
        keyframeEnabled: form.keyframeEnabled,
        frameSequenceEnabled: form.frameSequenceEnabled,
        trinityCardsEnabled: form.trinityCardsEnabled,
      };
      await directorAdminApi.updateModeSwitchConfig(patch);
      toast.success('已保存');
      const fresh = await directorAdminApi.getModeSwitchConfig();
      setForm(fresh);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-light-muted">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        加载中
      </div>
    );
  }

  return (
    <div className="card p-6 max-w-3xl space-y-5">
      <div>
        <h3 className="text-lg font-semibold mb-1">导演模式开关</h3>
        <p className="text-sm text-light-muted">
          控制用户在前端可以使用的分镜模式。关闭后，新建项目和分镜切换时将不再展示对应选项。
        </p>
      </div>

      <div className="space-y-3">
        <SwitchRow
          icon={<ImageIcon className="w-4 h-4 text-sky-500" />}
          label="单图模式"
          hint="每镜 1 张关键帧 → 1 段视频"
          checked={form.keyframeEnabled}
          onChange={(v) => update('keyframeEnabled', v)}
        />
        <SwitchRow
          icon={<Layers className="w-4 h-4 text-amber-500" />}
          label="多图模式"
          hint="每镜 N 张帧图 → 1 段视频"
          checked={form.frameSequenceEnabled}
          onChange={(v) => update('frameSequenceEnabled', v)}
        />
        <SwitchRow
          icon={<Sparkles className="w-4 h-4 text-violet-500" />}
          label="三合一模式"
          hint="角色卡 + 故事卡 + 视频卡 → 1 段视频"
          checked={form.trinityCardsEnabled}
          onChange={(v) => update('trinityCardsEnabled', v)}
        />
      </div>

      {!form.keyframeEnabled && !form.frameSequenceEnabled && !form.trinityCardsEnabled && (
        <div className="rounded-xl bg-rose-50 border border-rose-100 px-4 py-2.5 text-sm text-rose-600">
          至少需要开启一种导演模式，否则用户无法创建项目。
        </div>
      )}

      <div className="pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary px-5 py-2 flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          保存配置
        </button>
      </div>
    </div>
  );
}

function SwitchRow({
  icon,
  label,
  hint,
  checked,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-light-border bg-white px-4 py-3 transition hover:border-light-muted">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50">
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        <div className="text-[11px] text-slate-500">{hint}</div>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 shrink-0 accent-sky-600"
      />
    </label>
  );
}
